import { ApiError } from '@api/api-client';
import { createAppUser } from '@api/app-users-api';
import CountryPicker from '@components/country-picker';
import FieldError from '@components/field-error';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCountries } from '@hooks/use-countries';
import { type AddUserFormData, addUserSchema } from '@validation/schemas';
import { X } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { sileo } from 'sileo';

interface AddUserModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
] as const;

export default function AddUserModal({ onClose, onCreated }: AddUserModalProps): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const { countries } = useCountries();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<AddUserFormData>({
    resolver: zodResolver(addUserSchema),
    defaultValues: { username: '', email: '', name: '', countryId: '', role: 'editor' },
  });

  const onSubmit = async (data: AddUserFormData): Promise<void> => {
    setIsLoading(true);

    const selectedCountry = countries.find((c) => c.id === data.countryId);

    try {
      await createAppUser({
        username: data.username,
        email: data.email,
        name: data.name,
        country: selectedCountry?.code,
        role: data.role,
      });
      sileo.success({ title: 'User created successfully' });
      onCreated();
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const msg = error.message.toLowerCase();
        if (msg.includes('username')) {
          setError('username', { message: 'This username is already taken' });
        } else if (msg.includes('email')) {
          setError('email', { message: 'This email is already registered' });
        } else {
          sileo.error({ title: error.message });
        }
        return;
      }
      const message = error instanceof Error ? error.message : 'An error occurred';
      sileo.error({ title: message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClose();
      }}
      role="presentation"
    >
      <div
        className="modal"
        style={{ maxWidth: '520px' }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal__header">
          <h2 className="modal__title">Add User</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
          <div className="modal__body">
            <div className="modal-form">
              <div className="modal-form__row">
                <div className="form-group">
                  <label htmlFor="add-username" className="form-label">
                    Username *
                  </label>
                  <input
                    id="add-username"
                    type="text"
                    className={`form-input${errors.username ? ' form-input--error' : ''}`}
                    placeholder="john.doe"
                    {...register('username')}
                  />
                  <FieldError error={errors.username} />
                </div>

                <div className="form-group">
                  <label htmlFor="add-email" className="form-label">
                    Email *
                  </label>
                  <input
                    id="add-email"
                    type="email"
                    className={`form-input${errors.email ? ' form-input--error' : ''}`}
                    placeholder="john@example.com"
                    {...register('email')}
                  />
                  <FieldError error={errors.email} />
                </div>
              </div>

              <div className="modal-form__row">
                <div className="form-group">
                  <label htmlFor="add-name" className="form-label">
                    Name *
                  </label>
                  <input
                    id="add-name"
                    type="text"
                    className={`form-input${errors.name ? ' form-input--error' : ''}`}
                    placeholder="John Doe"
                    {...register('name')}
                  />
                  <FieldError error={errors.name} />
                </div>

                <div className="form-group">
                  <label htmlFor="add-role" className="form-label">
                    Role *
                  </label>
                  <select
                    id="add-role"
                    className={`form-select${errors.role ? ' form-select--error' : ''}`}
                    {...register('role')}
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <FieldError error={errors.role} />
                </div>
              </div>

              <div className="form-group">
                <span className="form-label">Country</span>
                <Controller
                  name="countryId"
                  control={control}
                  render={({ field }) => (
                    <CountryPicker value={field.value ?? ''} onChange={field.onChange} />
                  )}
                />
                <FieldError error={errors.countryId} />
              </div>

              <p className="form-hint">
                The user will be created without a password. They can set one via the &quot;Create
                Account&quot; page using their email.
              </p>
            </div>
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
